import { toast } from 'sonner';
import { API_ENDPOINTS, HTTP_METHODS, API_CONFIG } from '../config/api';
import { BaseApiService, ApiResponse, PaginatedResponse } from './baseApi';
import { Supplier } from './supplierApi';
import { RawItem } from './rawItemApi';
import { PoHeader } from './purchaseOrderApi';

export interface GRN {
  id: number;
  grnNumber: string;
  grnDate: string;
  receiveDate: string;
  poHeader: PoHeader;
  status: string;
  receivedBy: string;
  vehicleNumber: string;
  driverName: string;
  totalQuantity: number;
  totalAmount: number;
  notes: string;
  type: 'MANUAL' | 'PO';
  isActive: boolean;
  branchId: number;
  storeId: number | null;
  dcNo: string;
  dcDate: string;
  createdBy: number;
  updatedBy: number;
  createdAt: string;
  updatedAt: string;
  supplier: Supplier;
}

export interface GRNDetail {
  id: number;
  lineNumber: number;
  quantity: number;
  receivedQuantity: number;
  unitPrice: number;
  lineTotal: number;
  qualityCheck: string;
  remarks: string;
  heatNo: string;
  tcNo: string;
  tcDate: string;
  tcDocumentPath?: string | null; // TC document file path
  supplierHeatNo: string;
  nop?: number;
  branchId: number;
  storeId: number | null;
  createdBy: number;
  updatedBy: number;
  createdAt: string;
  updatedAt: string;
  grn: GRN;
  rawItem: RawItem;
  qualityStatus: string;
  qualityCheckComplete: boolean;
}

// GRN Consumption Detail interface (from BY_RAWITEM endpoint)
export interface GRNConsumptionDetail {
  heatNo: string;
  rawItemId: number;
  itemName: string;
  rawItemSku: string;
  originalQuantity: number;
  unitPrice: number;
  lineTotal: number | null;
  totalConsumed: number;
  remainingQuantity: number;
  issueCount: number;
  lastActivityDate: string;
  grnNumber: string;
  grnDate: string;
  supplierName: string;
}

// Batch request for raw item consumption
export interface RawItemConsumptionRequest {
  rawItemIds: number[];
  branchId?: number;
}

// Response for batch consumption (grouped by rawItemId)
export interface RawItemConsumptionResponse {
  [rawItemId: number]: GRNConsumptionDetail[];
}

export interface GRNResponse extends PaginatedResponse<GRN> {}

export interface GRNCreateRequest {
  grnNumber?: string; // Optional since it will be generated by server
  grnDate: string;
  receiveDate: string;
  poHeaderId: number;
  supplierId: number;
  status: string;
  receivedBy: string;
  vehicleNumber: string;
  driverName: string;
  totalQuantity?: number; // Optional since it will be calculated by server
  totalAmount?: number; // Optional since it will be calculated by server
  notes: string;
  type: 'MANUAL' | 'PO';
  isActive: boolean;
  branchId: number;
  storeId: number | null;
  dcNo: string;
  dcDate: string;
  createdBy: number;
  updatedBy: number;
}

export interface GRNUpdateRequest extends GRNCreateRequest {
  id: number;
}

export interface GRNDetailCreateRequest {
  lineNumber: number;
  quantity: number;
  receivedQuantity: number;
  unitPrice: number;
  lineTotal: number;
  qualityCheck: string;
  remarks: string;
  heatNo?: string; // Optional since it will be generated by server
  tcNo?: string | null;
  tcDate?: string | null;
  tcDocumentPath?: string | null; // TC document file path
  supplierHeatNo: string;
  nop?: number;
  branchId: number;
  storeId: number | null; // Can be null if store is not selected
  storeQty?: number; // Quantity to be allocated to store
  grnId: number;
  rawItemId: number;
  createdBy: number;
  updatedBy: number;
}

export interface GRNDetailUpdateRequest extends GRNDetailCreateRequest {
  id: number;
}

class GRNApiService extends BaseApiService {
  async getGRNs(branchId?: number, page: number = 1, pageSize: number = 10, searchTerm?: string, statusFilter?: string, startDate?: string, endDate?: string): Promise<GRNResponse> {
    try {
      let endpoint = API_ENDPOINTS.GRN.LIST;
      const params = new URLSearchParams();
      
      if (branchId) {
        params.append('branchId', branchId.toString());
      }
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (startDate) {
        params.append('startDate', startDate);
      }
      if (endDate) {
        params.append('endDate', endDate);
      }
      
      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }
      
      const response = await this.makeRequest<GRNResponse>(
        endpoint,
        { method: HTTP_METHODS.GET }
      );
      if (response.success) {
        return response;
      }
      throw new Error(response.message || 'Failed to fetch GRNs');
    } catch (error) {
      // console.error('Error fetching GRNs:', error);
      toast.error('Failed to fetch GRNs');
      throw error;
    }
  }

  async getGRNById(id: string): Promise<GRN> {
    try {
      const response = await this.makeRequest<ApiResponse<GRN>>(
        API_ENDPOINTS.GRN.DETAIL(id),
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch GRN');
      }
    } catch (error) {
      // console.error('Error fetching GRN:', error);
      toast.error('Failed to fetch GRN details');
      throw error;
    }
  }

  async getGRNDetailsByGRN(grnId: number): Promise<GRNDetail[]> {
    try {
      const response = await this.makeRequest<ApiResponse<GRNDetail[]>>(
        `${API_ENDPOINTS.GRN.GRN_DETAIL(grnId.toString())}`,
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch GRN details');
      }
    } catch (error) {
      // console.error('Error fetching GRN details:', error);
      toast.error('Failed to fetch GRN details');
      throw error;
    }
  }

  async createGRN(grnData: GRNCreateRequest): Promise<GRN> {
    try {
      const response = await this.makeRequest<ApiResponse<GRN>>(
        API_ENDPOINTS.GRN.CREATE,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(grnData)
        }
      );
      
      if (response.success) {
        toast.success('GRN created successfully');
        return response.data;
      } else {
        const message = response.message || 'Failed to create GRN';
        toast.error(message);
        throw new Error(message);
      }
    } catch (error) {
      const defaultMessage = 'Failed to create GRN';
      if (error instanceof Error) {
        // Extract backend message if wrapped by BaseApiService (e.g. "HTTP error! status: 400 - <msg>")
        const parts = error.message.split(' - ');
        const backendMessage = parts.length > 1 ? parts[parts.length - 1] : error.message;
        toast.error(backendMessage || defaultMessage);
      } else {
        toast.error(defaultMessage);
      }
      throw error;
    }
  }

  async updateGRN(id: string, grnData: GRNUpdateRequest): Promise<GRN> {
    try {
      const response = await this.makeRequest<ApiResponse<GRN>>(
        API_ENDPOINTS.GRN.UPDATE(id),
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(grnData)
        }
      );
      
      if (response.success) {
        toast.success('GRN updated successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update GRN');
      }
    } catch (error) {
      // console.error('Error updating GRN:', error);
      toast.error('Failed to update GRN');
      throw error;
    }
  }

  async deleteGRN(id: string): Promise<void> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.GRN.DELETE(id),
        { method: HTTP_METHODS.DELETE }
      );
      
      if (response.success) {
        toast.success('GRN deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete GRN');
      }
    } catch (error) {
      // console.error('Error deleting GRN:', error);
      toast.error('Failed to delete GRN');
      throw error;
    }
  }

  async createGRNDetail(grnDetailData: GRNDetailCreateRequest): Promise<GRNDetail> {
    try {
      // console.log('GRN Detail Create Payload:', grnDetailData);
      const response = await this.makeRequest<ApiResponse<GRNDetail>>(
        API_ENDPOINTS.GRN_DETAILS.CREATE,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(grnDetailData)
        }
      );
      
      if (response.success) {
        toast.success('GRN detail created successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to create GRN detail');
      }
    } catch (error) {
      // console.error('Error creating GRN detail:', error);
      toast.error('Failed to create GRN detail');
      throw error;
    }
  }

  async updateGRNDetail(id: string, grnDetailData: GRNDetailUpdateRequest): Promise<GRNDetail> {
    try {
      const response = await this.makeRequest<ApiResponse<GRNDetail>>(
        API_ENDPOINTS.GRN_DETAILS.UPDATE(id),
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(grnDetailData)
        }
      );
      
      if (response.success) {
        toast.success('GRN detail updated successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update GRN detail');
      }
    } catch (error) {
      // console.error('Error updating GRN detail:', error);
      toast.error('Failed to update GRN detail');
      throw error;
    }
  }

  async deleteGRNDetail(id: string): Promise<void> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.GRN_DETAILS.DELETE(id),
        { method: HTTP_METHODS.DELETE }
      );
      
      if (response.success) {
        toast.success('GRN detail deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete GRN detail');
      }
    } catch (error) {
      // console.error('Error deleting GRN detail:', error);
      toast.error('Failed to delete GRN detail');
      throw error;
    }
  }

  async searchGRNs(searchTerm: string, branchId?: number): Promise<GRN[]> {
    try {
      const params = new URLSearchParams();
      params.append('q', searchTerm);
      if (branchId) {
        params.append('branchId', branchId.toString());
      }
      
      const response = await this.makeRequest<ApiResponse<GRN[]>>(
        `${API_ENDPOINTS.GRN.SEARCH}?${params.toString()}`,
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to search GRNs');
      }
    } catch (error) {
      // console.error('Error searching GRNs:', error);
      toast.error('Failed to search GRNs');
      throw error;
    }
  }

  async approveGRN(id: string, approvedBy: number): Promise<GRN> {
    try {
      const params = new URLSearchParams();
      params.append('approvedBy', approvedBy.toString());
      
      const response = await this.makeRequest<ApiResponse<GRN>>(
        `${API_ENDPOINTS.GRN.APPROVE(id)}?${params.toString()}`,
        { method: HTTP_METHODS.POST }
      );
      
      if (response.success) {
        toast.success('GRN approved successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to approve GRN');
      }
    } catch (error) {
      // console.error('Error approving GRN:', error);
      toast.error('Failed to approve GRN');
      throw error;
    }
  }

  async getRemainingQuantityByHeatNumber(heatNumber: string): Promise<number> {
    try {
      const endpoint = API_ENDPOINTS.GRN_DETAILS.REMAINING_QUANTITY(heatNumber);
      // console.log('Calling API endpoint:', endpoint);
      // console.log('Full URL:', `${API_CONFIG.BASE_URL}${endpoint}`);
      
      const response = await this.makeRequest<ApiResponse<number>>(
        endpoint,
        { method: HTTP_METHODS.GET }
      );
      
      // console.log('API Response:', response);
      // console.log('Response data:', response.data);
      // console.log('Response success:', response.success);
      // console.log('Response message:', response.message);
      
      if (response.success) {
        // console.log('Extracted remaining quantity:', response.data);
        return response.data; // Data is directly the number
      } else {
        // console.error('API returned success: false with message:', response.message);
        throw new Error(response.message || 'Failed to fetch remaining quantity');
      }
    } catch (error) {
      // console.error('Error fetching remaining quantity for heat number:', heatNumber, error);
      if (error instanceof Error) {
        console.error('Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
      }
      // Don't show toast error here as this might be called frequently
      throw error;
    }
  }

  // Get GRN details by raw item IDs
  async searchGRNDetailsByRawItems(rawItemIds: number[], branchId?: number): Promise<GRNDetail[]> {
    try {
      const allGrnDetails: GRNDetail[] = [];
      
      // Fetch GRN details for each raw item ID
      for (const rawItemId of rawItemIds) {
        try {
          const params = new URLSearchParams();
          if (branchId) {
            params.append('branchId', branchId.toString());
          }
          
          const response = await this.makeRequest<ApiResponse<GRNDetail[]>>(
            `${API_ENDPOINTS.GRN_DETAILS.BY_RAWITEM(rawItemId.toString())}?${params.toString()}`,
            { method: HTTP_METHODS.GET }
          );
          
          if (response.success) {
            allGrnDetails.push(...response.data);
          } else {
            // console.warn(`Failed to fetch GRN details for raw item ${rawItemId}:`, response.message);
          }
        } catch (error) {
          // console.error(`Error fetching GRN details for raw item ${rawItemId}:`, error);
          // Continue with other raw items
        }
      }
      
      return allGrnDetails;
    } catch (error) {
      // console.error('Error fetching GRN details by raw items:', error);
      toast.error('Failed to fetch GRN details by raw items');
      throw error;
    }
  }

  // Get GRN consumption details for multiple raw items (batch)
  async getConsumptionByRawItemsBatch(rawItemIds: number[], branchId?: number): Promise<ApiResponse<RawItemConsumptionResponse>> {
    try {
      const requestPayload: RawItemConsumptionRequest = {
        rawItemIds,
        ...(branchId && { branchId })
      };

      const response = await this.makeRequest<ApiResponse<RawItemConsumptionResponse>>(
        API_ENDPOINTS.GRN_DETAILS.BY_RAWITEM_BATCH,
        { 
          method: HTTP_METHODS.POST,
          body: JSON.stringify(requestPayload)
        }
      );

      if (response.success) {
        return response;
      } else {
        throw new Error(response.message || 'Failed to fetch GRN consumption details');
      }
    } catch (error) {
      // console.error('Error fetching batch GRN consumption:', error);
      throw error;
    }
  }

  async exportGRNs(branchId?: number, status?: string, startDate?: string, endDate?: string): Promise<ApiResponse<GRN[]>> {
    try {
      const endpoint = API_ENDPOINTS.GRN.EXPORT(branchId, status, startDate, endDate);
      const url = `${API_CONFIG.BASE_URL}${endpoint}`;
      
      const token = localStorage.getItem('authToken');
      const response = await fetch(url, {
        method: HTTP_METHODS.GET,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('userInfo');
          window.location.href = '/login';
          throw new Error('Session expired. Please login again.');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      // Parse JSON response
      const jsonData = await response.json();
      return jsonData;
    } catch (error) {
      // console.error('Error exporting GRNs:', error);
      toast.error('Failed to export GRNs');
      throw error;
    }
  }
}

export const grnApiService = new GRNApiService();
